import { useMemo } from 'react';
import { useCustom, useDelete, useGo } from '@refinedev/core';
import { notify } from '@/lib/notify';
import {
  Mail,
  MessageSquare,
  Pencil,
  Plus,
  Trash2,
  Star,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { DataTable, type Column } from '@/components/tables';
import { FormHeader } from '@/components/forms';
import { useConfirm } from '@/hooks/use-confirm';
import { useScrollRestoration } from '@/hooks/use-scroll-restoration';
import {
  ALL_MESSAGE_TEMPLATE_TYPES,
  MESSAGE_TEMPLATE_CONFIG,
  MessageTemplateType,
  MESSAGE_TEMPLATE_LANGS,
  type MessageTemplate,
  type MessageTemplateType as MessageTemplateTypeValue,
} from './types';

/** Сгруппированная строка singleton-типа: тип + все его языки. */
interface SingletonGroup {
  id: string;
  type: MessageTemplateTypeValue;
  label: string;
  description: string;
  langs: { lang: string; isActive: boolean }[];
  /** ID любой строки этого типа — для перехода в редактор. */
  editId: number | null;
}

function isMessageTemplateArray(value: unknown): value is MessageTemplate[] {
  return Array.isArray(value);
}

export function MessageTemplatesList() {
  useScrollRestoration();
  const go = useGo();
  const { confirm, ConfirmationDialog } = useConfirm();

  // Все singleton-шаблоны (тип × язык).
  const singletonResult = useCustom<MessageTemplate[]>({
    url: '/admin/message-templates',
    method: 'get',
  });

  // Именованные шаблоны Telegram-приглашений.
  const namedResult = useCustom<MessageTemplate[]>({
    url: `/admin/message-templates/by-type/${MessageTemplateType.INVITATION_TELEGRAM}`,
    method: 'get',
  });

  const { mutate: deleteNamed } = useDelete();

  const isLoading =
    (singletonResult.query?.isLoading ?? false) ||
    (namedResult.query?.isLoading ?? false);

  const singletonRows = useMemo<MessageTemplate[]>(() => {
    const payload = singletonResult.result?.data;
    return isMessageTemplateArray(payload) ? payload : [];
  }, [singletonResult.result]);

  const namedRows = useMemo<MessageTemplate[]>(() => {
    const payload = namedResult.result?.data;
    return isMessageTemplateArray(payload) ? payload : [];
  }, [namedResult.result]);

  const singletonGroups = useMemo<SingletonGroup[]>(() => {
    return ALL_MESSAGE_TEMPLATE_TYPES.filter(
      (type) => MESSAGE_TEMPLATE_CONFIG[type].multiplicity === 'singleton',
    ).map((type) => {
      const config = MESSAGE_TEMPLATE_CONFIG[type];
      const typeRows = singletonRows.filter((row) => row.type === type);
      const langs = MESSAGE_TEMPLATE_LANGS.map((langMeta) => {
        const row = typeRows.find((r) => r.lang === langMeta.code);
        return {
          lang: langMeta.code,
          isActive: row ? row.isActive : false,
        };
      }).filter((entry) => typeRows.some((r) => r.lang === entry.lang));

      return {
        id: type,
        type,
        label: config.label,
        description: config.description,
        langs,
        editId: typeRows[0]?.id ?? null,
      };
    }).filter((group) => group.langs.length > 0);
  }, [singletonRows]);

  const handleEditSingleton = (group: SingletonGroup) => {
    if (group.editId === null) {
      return;
    }
    go({ to: `/message-templates/edit/${group.editId}` });
  };

  const handleDeleteNamed = async (template: MessageTemplate) => {
    if (template.isDefault) {
      notify.error('Нельзя удалить шаблон по умолчанию');
      return;
    }
    const confirmed = await confirm({
      title: `Удалить шаблон «${template.name ?? template.slug ?? template.id}»?`,
      description: 'Это действие нельзя отменить.',
      variant: 'destructive',
      confirmText: 'Удалить',
      cancelText: 'Отмена',
    });
    if (!confirmed) {
      return;
    }
    deleteNamed(
      { resource: 'message-templates', id: `named/${template.id}` },
      {
        onSuccess: () => {
          void namedResult.query?.refetch();
        },
        onError: (error) => {
          notify.error(
            error instanceof Error ? error.message : 'Не удалось удалить шаблон',
          );
        },
      },
    );
  };

  const singletonColumns: Column<SingletonGroup>[] = [
    {
      key: 'label',
      header: 'Шаблон',
      render: (_, group) => (
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-muted-foreground" />
          <div className="flex flex-col">
            <span className="font-medium">{group.label}</span>
            <span className="text-xs text-muted-foreground">
              {group.description}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: 'langs',
      header: 'Языки',
      render: (_, group) => (
        <div className="flex flex-wrap gap-1">
          {group.langs.map((entry) => (
            <Badge
              key={entry.lang}
              variant={entry.isActive ? 'default' : 'outline'}
              className="uppercase"
            >
              {entry.lang}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      key: 'active',
      header: 'Активен',
      render: (_, group) => {
        const activeCount = group.langs.filter((l) => l.isActive).length;
        if (activeCount === group.langs.length) {
          return (
            <Badge className="bg-green-500/10 text-green-500">Все языки</Badge>
          );
        }
        if (activeCount === 0) {
          return <Badge variant="secondary">Выключен</Badge>;
        }
        return (
          <Badge className="bg-yellow-500/10 text-yellow-500">
            {activeCount}/{group.langs.length}
          </Badge>
        );
      },
    },
    {
      key: 'edit',
      header: '',
      width: '160px',
      render: (_, group) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleEditSingleton(group);
          }}
        >
          <Pencil className="h-4 w-4 mr-1" />
          Редактировать
        </Button>
      ),
    },
  ];

  const namedColumns: Column<MessageTemplate>[] = [
    {
      key: 'name',
      header: 'Название',
      render: (_, template) => (
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">
            {template.name ?? template.slug ?? `#${template.id}`}
          </span>
          {template.isDefault && (
            <Badge variant="secondary" className="gap-1">
              <Star className="h-3 w-3" />
              По умолчанию
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'slug',
      header: 'Slug',
      width: '180px',
      render: (_, template) => (
        <code className="text-sm bg-muted px-2 py-1 rounded">
          {template.slug ?? '—'}
        </code>
      ),
    },
    {
      key: 'isActive',
      header: 'Статус',
      width: '120px',
      render: (_, template) => (
        <Badge variant={template.isActive ? 'default' : 'outline'}>
          {template.isActive ? 'Активен' : 'Неактивен'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '200px',
      render: (_, template) => (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              go({ to: `/message-templates/edit/${template.id}` });
            }}
          >
            <Pencil className="h-4 w-4 mr-1" />
            Изменить
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive"
            onClick={(e) => {
              e.stopPropagation();
              void handleDeleteNamed(template);
            }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  const tgConfig = MESSAGE_TEMPLATE_CONFIG[MessageTemplateType.INVITATION_TELEGRAM];

  return (
    <div className="space-y-6">
      <FormHeader
        title="Шаблоны сообщений"
        description="Системные письма и сообщения: код входа, приглашения, инвойсы, уведомления"
        sticky
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Mail className="h-4 w-4" />
            Письма и системные сообщения
          </CardTitle>
          <CardDescription>
            Один шаблон на тип и язык. Используются автоматически по типу события.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={singletonColumns}
            data={singletonGroups}
            total={singletonGroups.length}
            page={1}
            pageSize={singletonGroups.length || 1}
            isLoading={isLoading}
            onPageChange={() => {}}
            onRowClick={handleEditSingleton}
            emptyMessage="Шаблоны не найдены"
            rowKey="id"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div className="space-y-1.5">
            <CardTitle className="flex items-center gap-2 text-base">
              <MessageSquare className="h-4 w-4" />
              {tgConfig.label}
            </CardTitle>
            <CardDescription>{tgConfig.description}</CardDescription>
          </div>
          <Button
            size="sm"
            onClick={() =>
              go({
                to: `/message-templates/create?type=${MessageTemplateType.INVITATION_TELEGRAM}`,
              })
            }
          >
            <Plus className="h-4 w-4 mr-2" />
            Создать
          </Button>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={namedColumns}
            data={namedRows}
            total={namedRows.length}
            page={1}
            pageSize={namedRows.length || 1}
            isLoading={isLoading}
            onPageChange={() => {}}
            onRowClick={(template) =>
              go({ to: `/message-templates/edit/${template.id}` })
            }
            emptyMessage="Нет именованных шаблонов"
            rowKey="id"
          />
        </CardContent>
      </Card>

      {ConfirmationDialog}
    </div>
  );
}
